import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import { GenericFormData, ValidationErrors } from '@platform-ui-common/hooks/useForm/index.js';
import useUsStates from '@platform-ui-common/hooks/useUsStates.js';
import { getAddressKey } from '@platform-ui-common/utilities/address.js';

interface ManualAddressInputProps<FormData extends GenericFormData> {
  addressKey: string;
  answers: FormData;
  customStreetAddressLabel?: string;
  disabled?: boolean;
  errors: ValidationErrors<FormData>;
  handleChange: (key: string, value: string) => void;
  readOnly?: boolean;
  required: boolean;
  showErrorMessages: boolean;
  showStreetAddress: boolean;
}

export default function ManualAddressInput<FormData extends GenericFormData>({
  addressKey,
  answers,
  customStreetAddressLabel,
  disabled,
  errors,
  handleChange,
  readOnly,
  required,
  showErrorMessages,
  showStreetAddress,
}: ManualAddressInputProps<FormData>): JSX.Element {
  const usStates = useUsStates();
  const getError = (field: string): string | boolean => {
    const error = errors[getAddressKey(field, addressKey)];
    return showErrorMessages ? (error as string) : !!error;
  };

  return (
    <>
      {showStreetAddress && (
        <>
          <TextInput
            id={`${addressKey}-addressLine1`}
            label={customStreetAddressLabel ? customStreetAddressLabel : 'Street Address'}
            value={answers[getAddressKey('addressLine1', addressKey)] ?? ''}
            onChange={(value: string): void =>
              handleChange(getAddressKey('addressLine1', addressKey), value)
            }
            required={required}
            inputProps={{ readOnly }}
            disabled={disabled}
            error={getError('addressLine1')}
          />
          <TextInput
            id={`${addressKey}-addressLine2`}
            label="Apartment/Suite #"
            value={answers[getAddressKey('addressLine2', addressKey)] ?? ''}
            onChange={(value: string): void =>
              handleChange(getAddressKey('addressLine2', addressKey), value)
            }
            inputProps={{ readOnly }}
            disabled={disabled}
            error={getError('addressLine2')}
          />
        </>
      )}
      <TextInput
        id={`${addressKey}-city`}
        label="City"
        value={answers[getAddressKey('city', addressKey)] ?? ''}
        onChange={(value: string): void => handleChange(getAddressKey('city', addressKey), value)}
        required={required}
        inputProps={{ readOnly }}
        disabled={disabled}
        error={getError('city')}
      />
      <div className="grid w-full grid-cols-2 max-w-field gap-x-4">
        <Dropdown
          id={`${addressKey}-state`}
          label="State"
          value={answers[getAddressKey('state', addressKey)] ?? ''}
          onChange={(value: string): void =>
            handleChange(getAddressKey('state', addressKey), value)
          }
          items={usStates}
          placeholder="Select State"
          required={required}
          inputProps={{ readOnly }}
          disabled={disabled}
          error={getError('state')}
        />
        <TextInput
          id={`${addressKey}-zip`}
          label="Zip Code"
          value={answers[getAddressKey('zip', addressKey)] ?? ''}
          onChange={(value: string): void => handleChange(getAddressKey('zip', addressKey), value)}
          required={required}
          inputProps={{ readOnly }}
          disabled={disabled}
          error={getError('zip')}
        />
      </div>
    </>
  );
}
